import React from "react";
import { motion } from "framer-motion";
import { DemoForm } from "@/components/marketing/DemoModal";
import { Plugs, ArrowsClockwise, Handshake, Check, Cube, Buildings } from "@phosphor-icons/react";
import {
  fadeUp, stagger, Reveal, Overline, EditorialHero, SectionHeader, GlassCard,
} from "@/components/marketing/editorial";

const ERPS = [
  { n: "Oracle Fusion", d: "Native REST integration across Financials, Procurement, Projects and HCM. Reads and writes live transactions with full audit trails." },
  { n: "Oracle EBS", d: "Connect E-Business Suite GL, AP, AR and Assets. Execute journals, invoices and reconciliations directly inside EBS." },
  { n: "Microsoft Dynamics 365", d: "Finance & Operations connectors for ledger, vendors, customers and reporting — automate close and P2P end-to-end." },
  { n: "IFS", d: "Integrate IFS Cloud finance, procurement and projects to run reconciliations, approvals and analytics autonomously." },
];

const TIERS = [
  { icon: Plugs, t: "Technology Partners", d: "Build and certify native connectors to your ERP, EPM or data platform on Vaku's open integration framework." },
  { icon: Handshake, t: "Implementation Partners", d: "Deliver Vaku to enterprise finance teams — co-sell, implement and support deployments across your client base." },
  { icon: Buildings, t: "Advisory & Alliances", d: "Embed Vaku into your finance transformation and managed-services practice with joint go-to-market." },
];

export default function PartnersPage() {
  return (
    <>
      <EditorialHero
        eyebrow="Partners & integrations"
        title="One AI, connected to"
        italic="every system you run."
        sub="Vaku integrates natively with leading ERP and EPM platforms and partners with the firms that implement them. Read and write directly — no CSV exports, no sync delays."
      />

      {/* ERP integration partners — white */}
      <section className="py-24 sm:py-32 border-t bg-white"
               style={{ borderColor: "rgba(11,30,63,0.10)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader eyebrow="Integration partners"
            title="Native connectors for the platforms you" italic="already use." />
          <motion.div className="grid md:grid-cols-2 gap-5"
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            {ERPS.map((e) => (
              <motion.div key={e.n} variants={fadeUp}>
                <GlassCard testid={`partner-erp-${e.n.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div className="flex items-center gap-3">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl"
                         style={{ background: "rgba(200,162,75,0.12)", border: "1px solid rgba(200,162,75,0.40)", color: "#C8A24B" }}>
                      <Cube size={20} weight="duotone" />
                    </div>
                    <h3 className="font-serif text-2xl font-medium" style={{ color: "#0B1E3F" }}>{e.n}</h3>
                  </div>
                  <p className="text-sm mt-4 leading-relaxed font-light" style={{ color: "rgba(11,30,63,0.66)" }}>{e.d}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "#1F6B4A" }}>
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#1F6B4A" }} />
                    <ArrowsClockwise size={14} weight="bold" /> Read &amp; write
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Alliance tiers — navy band */}
      <section className="py-24 sm:py-32 border-t"
               style={{ background: "#0B1E3F", borderColor: "rgba(11,30,63,0.10)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-3xl mb-16">
            <Overline>Alliance program</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mt-4 leading-[1.05] text-white">
              Partner with <span className="italic" style={{ color: "#C8A24B" }}>Vaku.</span>
            </h2>
          </Reveal>
          <motion.div className="grid md:grid-cols-3 gap-5"
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            {TIERS.map(({ icon: Icon, t, d }) => (
              <motion.div key={t} variants={fadeUp}
                data-testid={`partner-tier-${t.replace(/\s+/g, '-').toLowerCase()}`}
                className="rounded-2xl p-8 h-full transition-all hover:-translate-y-1"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(200,162,75,0.30)" }}>
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl"
                     style={{ background: "rgba(200,162,75,0.15)", border: "1px solid rgba(200,162,75,0.45)", color: "#C8A24B" }}>
                  <Icon size={22} weight="duotone" />
                </div>
                <h3 className="font-serif text-2xl font-medium mt-5 text-white">{t}</h3>
                <p className="text-sm mt-3 leading-relaxed font-light text-white/70">{d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Application form — white */}
      <section className="bg-white py-24 sm:py-32 border-t" style={{ borderColor: "rgba(11,30,63,0.10)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <Reveal>
              <div className="text-[11px] font-mono uppercase tracking-[0.25em] font-semibold" style={{ color: "#C8A24B" }}>Become a partner</div>
              <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[1.02]" style={{ color: "#0B1E3F" }}>
                Apply to the <span className="italic" style={{ color: "#C8A24B" }}>Vaku partner network</span>.
              </h2>
              <p className="font-light mt-6 leading-relaxed" style={{ color: "rgba(11,30,63,0.70)" }}>
                Tell us about your firm and the platforms you work with. Our partnerships team will reach out within 24 hours.
              </p>
              <ul className="mt-8 space-y-4">
                {["Co-sell and co-market with the Vaku partnerships team",
                  "Early access to new ERP & EPM connectors",
                  "Dedicated enablement and certification"].map((t) => (
                    <li key={t} className="flex gap-3" style={{ color: "rgba(11,30,63,0.80)" }}>
                      <Check size={20} weight="bold" className="shrink-0 mt-0.5" style={{ color: "#1F6B4A" }} />
                      <span className="font-light">{t}</span>
                    </li>
                  ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-2xl bg-white p-8 sm:p-10 shadow-[0_20px_60px_rgba(11,30,63,0.08)]"
                   style={{ border: "1px solid rgba(11,30,63,0.10)" }}>
                <DemoForm source="partner-application" testidPrefix="partner-demo" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
